import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight, Mail } from "lucide-react";
import Ornament, { SectionOverline } from "../components/Ornament";
import { api } from "../lib/config";
import useSEO from "../hooks/useSEO";

/**
 * Public FAQ page.
 * Reads FAQs from the same admin-managed collection used in the home page section
 * and adds a schema.org FAQPage JSON-LD so it is eligible for rich results and
 * generative search citations.
 */
const FaqPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/faqs");
        setFaqs(r.data || []);
      } catch (e) { console.error(e); }
    })();
  }, []);

  // Build FAQPage JSON-LD when data is loaded
  const jsonLd = faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": { "@type": "Answer", "text": f.answer },
    })),
  } : null;

  useSEO({
    title: "Foire aux questions",
    description: "Toutes les réponses à vos questions sur la gravure et la découpe laser sur mesure : délais, matériaux, tarifs, livraison, personnalisation. Passion Verre, Isère.",
    jsonLd,
  });

  return (
    <div data-testid="faq-page">
      {/* HERO */}
      <section className="pt-32 pb-16 bg-[#171717]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <SectionOverline className="mb-4">Foire aux questions</SectionOverline>
          <h1 className="font-serif text-5xl sm:text-6xl text-[#DDD4B2] leading-[1.05]">Vos questions,<br />nos réponses.</h1>
          <p className="text-sm text-[#DDD4B2]/70 mt-6 max-w-2xl mx-auto leading-relaxed">
            Retrouvez ici les réponses aux questions les plus fréquentes sur nos techniques,
            nos matériaux, nos délais et notre livraison. Vous ne trouvez pas votre réponse ?
            Contactez-nous directement.
          </p>
          <Ornament className="mt-8 mx-auto" />
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 sm:py-24 bg-[#171717]">
        <div className="max-w-3xl mx-auto px-6">
          {faqs.length === 0 && (
            <p className="text-center text-[#DDD4B2]/60 text-sm">Chargement…</p>
          )}
          <div className="space-y-4">
            {faqs.map((f, i) => {
              const isOpen = openId === f.id;
              return (
                <motion.div
                  key={f.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                  className="border border-[#9A8640]/25"
                  data-testid={`faq-item-${f.id}`}
                >
                  <button
                    onClick={() => setOpenId(isOpen ? null : f.id)}
                    className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left hover:bg-[#9A8640]/5 transition-colors"
                    aria-expanded={isOpen}
                    data-testid={`faq-toggle-${f.id}`}
                  >
                    <span className="font-serif text-xl text-[#DDD4B2] leading-snug">
                      {f.question}
                    </span>
                    <ChevronDown
                      size={20}
                      className={`text-[#9A8640] shrink-0 mt-1 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-6 pt-1 text-sm text-[#DDD4B2]/85 leading-relaxed whitespace-pre-wrap">
                          {f.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0f0f0f] border-t border-[#9A8640]/20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#DDD4B2] mb-4">
            Une question qui n'est pas dans la liste ?
          </h2>
          <p className="text-sm text-[#DDD4B2]/70 mb-8 max-w-xl mx-auto leading-relaxed">
            Chaque projet est unique. Envoyez-nous vos idées, croquis ou références,
            nous vous répondons personnellement sous 48 h.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/contact"
              className="btn-gold px-8 py-4 text-sm uppercase inline-flex items-center gap-3"
              data-testid="faq-cta-contact"
            >
              <Mail size={16} /> Nous écrire
            </Link>
            <Link
              to="/realisations"
              className="btn-outline px-8 py-4 text-sm uppercase inline-flex items-center gap-3"
              data-testid="faq-cta-realisations"
            >
              Voir nos créations <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FaqPage;
